import { call, all, put, takeLatest, select } from 'redux-saga/effects';

import api from '../../../services/api';
import notification from '../../../services/notification';
import history from '../../../services/history';
import teamService from '../../../services/modules/team/team-service';
import * as teamActions from './team-actions';
import { GET_ALL_REQUEST, GET_BY_ID_REQUEST, SAVE_REQUEST, EDIT_ITEM, DELETE_ITEM } from './team-constants';
import { ISA_ERROR } from '../../../constants';
import domainService from '../../../services/modules/domain/domain-service';
import { formatMessage } from '~/i18n/translate';
import { ConfigurationInterface } from '@e3labs/isa-data-dictionary';
import { AppStateType } from '../root-reducer';
import {
  ensureSelectData,
  loadDomainSelects,
  ensureRestrictions
} from '../select-pagination/select-pagination-service';
import { appendSelectData } from '../select-pagination/select-pagination-actions';

const INITIAL_VALUES = {
  description: '',
  name: '',
  usersIDs: [],
  users: [],
  workingHours: {
    enabled: false,
    friday: {
      enabled: true,
      startFirstShift: '00:00',
      endFirstShift: '00:00',
      startSecondShift: '00:00',
      endSecondShift: '00:00'
    },
    monday: {
      enabled: true,
      startFirstShift: '00:00',
      endFirstShift: '00:00',
      startSecondShift: '00:00',
      endSecondShift: '00:00'
    },
    saturday: {
      enabled: false,
      startFirstShift: '00:00',
      endFirstShift: '00:00',
      startSecondShift: '00:00',
      endSecondShift: '00:00'
    },
    sunday: {
      enabled: false,
      startFirstShift: '00:00',
      endFirstShift: '00:00',
      startSecondShift: '00:00',
      endSecondShift: '00:00'
    },
    thursday: {
      enabled: true,
      startFirstShift: '00:00',
      endFirstShift: '00:00',
      startSecondShift: '00:00',
      endSecondShift: '00:00'
    },
    tuesday: {
      enabled: true,
      startFirstShift: '00:00',
      endFirstShift: '00:00',
      startSecondShift: '00:00',
      endSecondShift: '00:00'
    },
    wednesday: {
      enabled: true,
      startFirstShift: '00:00',
      endFirstShift: '00:00',
      startSecondShift: '00:00',
      endSecondShift: '00:00'
    }
  }
};

const sort = items => {
  return [...items]?.sort((a: any, b: any) => a.externalCode - b.externalCode);
};

export function* getAll() {
  try {
    yield put(teamActions.setLoadingData(true));

    const { data } = yield call(teamService.getTeams);
    if (data) {
      yield put(teamActions.getAllSuccess([...data]));
    }

    yield put(teamActions.setLoadingData(false));
  } catch (error) {
    yield put(teamActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(teamActions.setLoadingData(true));
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

    const [{ data: users }, domains] = yield all([call(api.get, '/user/'), call(loadDomainSelects)]);

    const selects = { users };

    if (!id) {
      yield put(
        teamActions.getByIdSuccess({
          data: {
            ...INITIAL_VALUES,
            allowVendorTracking:
              configuration?.allowVendorTracking || configuration?.allowManualVendorTracking ? true : false,
            allowTeamAccessKit: configuration?.allowKitOnlyOnOrder ? true : false
          },
          selects
        })
      );
    } else {
      const { data } = yield call(teamService.getTeam, id);
      if (data) {
        const { productRestrictions, ...rest } = data;
        const ensured = yield* ensureRestrictions(productRestrictions, domains);

        yield put(
          teamActions.getByIdSuccess({
            data: {
              ...rest,
              users: data?.usersIDs?.map((s: any) => users.find((t: any) => t._id === s)),
              workingHoursEnabled: data?.workingHours?.enabled,
              providers: ensured.providers,
              departiments: ensured.departments,
              sections: ensured.sections,
              brands: ensured.brands,
              categories: ensured.categories,
              productLines: ensured.productLines
            },
            selects
          })
        );
      }
    }
    yield put(teamActions.setLoadingData(false));
  } catch (error) {
    yield put(teamActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload } = data;
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

    yield put(teamActions.setLoadingSend(true));

    const { users, productLines, providers, sections, brands, categories, departiments, ...rest } = payload;

    const team = {
      ...rest,
      users: users?.filter(value => value !== undefined),
      usersIDs: users?.filter(value => value !== undefined)?.map((s: any) => s._id),
      productRestrictions: {
        productLineExternalCode: productLines?.length > 0 ? [...productLines?.map(p => p.externalCode)] : [],
        providerExternalCode: providers?.length > 0 ? [...providers?.map(p => p.externalCode)] : [],
        sectionExternalCode: sections?.length > 0 ? [...sections?.map(p => p.externalCode)] : [],
        brandExternalCode: brands?.length > 0 ? [...brands?.map(p => p.externalCode)] : [],
        categoryExternalCode: categories?.length > 0 ? [...categories?.map(p => p.externalCode)] : [],
        departmentExternalCode: departiments?.length > 0 ? [...departiments?.map(p => p.externalCode)] : []
      },
      allowVendorTracking:
        configuration?.allowVendorTracking || configuration?.allowManualVendorTracking
          ? payload?.allowVendorTracking === undefined
            ? true
            : payload?.allowVendorTracking
          : payload?.allowVendorTracking
          ? payload?.allowVendorTracking
          : false,
      allowTeamAccessKit: configuration?.allowKitOnlyOnOrder
        ? payload?.allowTeamAccessKit === undefined
          ? true
          : payload?.allowTeamAccessKit
        : payload?.allowTeamAccessKit
        ? payload?.allowTeamAccessKit
        : false
    };

    if (id) {
      yield call(teamService.editTeam, { _id: id, ...team });
    } else {
      yield call(teamService.createTeam, team);
    }
    yield call(history.push, `/access/team/`, {
      date: new Date().getTime()
    });
    yield put(teamActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('teamFormUpdatedSuccess') : formatMessage('teamFormCreatedSuccess')
    );
  } catch (error) {
    yield put(teamActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(teamActions.setLoadingSend(true));
      yield call(teamService.deleteTeam, id);
      yield put(teamActions.setLoadingSend(false));

      yield put(teamActions.getAllRequest());
      yield call(notification.success, formatMessage('teamFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(teamActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/access/team/edit/${id}`);
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem)
]);
